import { RestaurantAttendance } from '../types/restaurantPersonnel';
import { formatCivilDate, formatCivilDateTime, formatHours } from './restaurantPersonnelFormat';

export interface PayrollHistoryItem {
  year: number;
  month: number;
  status: 'approved' | 'closed';
  monthlyRevenueSnapshot: number;
  totalSalaryHoursSnapshot: number;
  approvedAtUtc: string | null;
  closedAtUtc: string | null;
  employeeName: string;
  positionSnapshot: string;
  calculationMethod: string;
  workedHours: number;
  hourlyRate: number;
  evaluationCoefficient: number;
  salaryHours: number;
  baseSalary: number;
  fixedBonusSnapshot: number;
  bonusPercentSnapshot: number;
  bonus: number;
  fixedPenaltySnapshot: number;
  penaltyPercentSnapshot: number;
  penalty: number;
  fixedMonthlyRate: number;
  revenuePercent: number;
  monthlyRevenue: number;
  amount: number;
  adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
}

interface EmployeePrintInput {
  month: string;
  employee: { fullName: string; position: string; attendance: RestaurantAttendance[] };
  canViewSalary: boolean;
  payrollHistory: PayrollHistoryItem[] | null;
  payrollHistoryLoading: boolean;
  payrollHistoryError: string;
}

const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character] ?? character));
const money = (value: number) => `${new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(value)} ₴`;
const decimal = (value: number) => formatHours(value, 4, false);
const row = (label: string, value: string) =>
  `<tr><th scope="row">${escapeHtml(label)}</th><td class="number">${escapeHtml(value)}</td></tr>`;
const methods: Record<string, string> = {
  'hourly-coefficient': 'Погодинна ставка × коефіцієнт',
  'fixed-plus-revenue': 'Фіксована сума + частка виторгу',
  'revenue-weighted-hours': 'Фонд за зваженими годинами',
  'halls-fund': 'Ресторанний фонд HALLS',
};

function salarySection(input: EmployeePrintInput): string {
  if (!input.canViewSalary)
    return '<p class="notice">Зарплатні дані недоступні без окремого дозволу на перегляд зарплати.</p>';
  if (input.payrollHistoryError || input.payrollHistoryLoading || input.payrollHistory === null)
    return `<p class="notice">${escapeHtml(input.payrollHistoryError || (input.payrollHistoryLoading
      ? 'Історія зарплати ще завантажується. Повторіть друк після завершення завантаження.'
      : 'Історія зарплати недоступна.'))}</p>`;
  const [year, month] = input.month.split('-').map(Number);
  const period = input.payrollHistory.find(item => item.year === year && item.month === month &&
    (item.status === 'approved' || item.status === 'closed'));
  if (!period)
    return '<p class="notice">Зарплату за вибраний місяць ще не фіналізовано: затвердженого або закритого знімка немає. Поточні оцінки та інші місяці не підставляються.</p>';

  const parameters = period.calculationMethod === 'fixed-plus-revenue'
    ? row('Фіксована місячна ставка', money(period.fixedMonthlyRate)) +
      row('Частка виторгу', `${decimal(period.revenuePercent)} %`) +
      row('Виторг ресторану у знімку', money(period.monthlyRevenue))
    : row('Відпрацьовано у знімку', formatHours(period.workedHours)) +
      row('Коефіцієнт оцінювання у знімку', decimal(period.evaluationCoefficient)) +
      row('Розрахункові години у знімку', formatHours(period.salaryHours)) +
      row('Ставка за годину у знімку', `${money(period.hourlyRate)} / год`);
  const auditDate = (value: string) => `${formatCivilDateTime(value)} UTC`;
  return `<div class="salary-meta"><strong>${period.status === 'closed' ? 'Закритий період' : 'Затверджений період'}</strong>
    <p>Працівник у знімку: ${escapeHtml(period.employeeName)} · ${escapeHtml(period.positionSnapshot)}</p>
    <p>${escapeHtml(methods[period.calculationMethod] ?? period.calculationMethod)}</p>
    ${period.approvedAtUtc ? `<p>Затверджено: ${escapeHtml(auditDate(period.approvedAtUtc))}</p>` : ''}
    ${period.closedAtUtc ? `<p>Закрито: ${escapeHtml(auditDate(period.closedAtUtc))}</p>` : ''}</div>
    <table class="salary"><caption>Параметри та деталізація нарахування</caption>
      <thead><tr><th scope="col">Складова</th><th scope="col" class="number">Значення</th></tr></thead>
      <tbody>${parameters}
        ${row('Базове нарахування', money(period.baseSalary))}
        ${row('Бонус: фіксована частина', money(period.fixedBonusSnapshot))}
        ${row('Бонус: відсоток', `${decimal(period.bonusPercentSnapshot)} %`)}
        ${row('Бонус разом', money(period.bonus))}
        ${row('Штраф: фіксована частина', money(period.fixedPenaltySnapshot))}
        ${row('Штраф: відсоток', `${decimal(period.penaltyPercentSnapshot)} %`)}
        ${row('Штраф разом (утримання)', money(period.penalty))}
      </tbody><tfoot>${row('Нараховано за знімком', money(period.amount))}</tfoot>
    </table>
    <p class="muted">Усі суми та параметри збережені у фіналізованому знімку, без перерахунку.
      Табель нижче відображає збережені інтервали за місяць; години знімка наведені окремо.</p>
    ${period.adjustments.length ? `<table><caption>Окремі коригування після закриття</caption>
      <thead><tr><th scope="col">Дата (UTC)</th><th scope="col">Причина / автор</th><th scope="col" class="number">Сума (+/−)</th></tr></thead>
      <tbody>${period.adjustments.map(item => `<tr><td>${escapeHtml(auditDate(item.createdAtUtc))}</td>
        <td>${escapeHtml(item.reason)}<br><span class="muted">${escapeHtml(item.createdByUsername)}</span></td>
        <td class="number">${escapeHtml(money(item.amount))}</td></tr>`).join('')}</tbody></table>
      <p class="muted">Коригування наведено окремо: вони не змінюють суму первинного знімка вище.</p>` : ''}
  `;
}

export function buildRestaurantEmployeePrintHtml(input: EmployeePrintInput): string {
  const [year, month] = input.month.split('-').map(Number);
  const monthLabel = new Intl.DateTimeFormat('uk-UA', { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1));
  const attendance = input.employee.attendance
    .filter(item => item.workDate.slice(0, 7) === input.month)
    .sort((a, b) => a.workDate.localeCompare(b.workDate) || a.checkInAt.localeCompare(b.checkInAt) || a.id - b.id);
  const days = new Set(attendance.map(item => item.workDate.slice(0, 10))).size;
  const openCount = attendance.filter(item => !item.checkOutAt).length;
  const totalHours = attendance.reduce((sum, item) => sum + item.hoursWorked, 0);
  const attendanceRows = attendance.map(item => `<tr>
    <th scope="row">${escapeHtml(formatCivilDate(item.workDate))}</th>
    <td>${escapeHtml(formatCivilDateTime(item.checkInAt))}</td>
    <td>${escapeHtml(item.checkOutAt ? formatCivilDateTime(item.checkOutAt) : 'Не завершено')}</td>
    <td class="number">${escapeHtml(decimal(item.hoursWorked))}</td>
    <td class="number">${escapeHtml(decimal(item.hourCoefficient))}</td>
    <td>${escapeHtml(item.comment)}</td></tr>`).join('');
  return `<!doctype html><html lang="uk"><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${escapeHtml(`Бланк працівника — ${input.employee.fullName} — ${input.month}`)}</title>
    <style>
      @page { size: A4 portrait; margin: 14mm 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #172b3a; background: white; font: 10pt/1.45 Arial, sans-serif; }
      header { border-top: 5px solid #244e60; border-bottom: 1px solid #244e60; padding: 14px 0; margin-bottom: 18px; }
      .eyebrow { text-transform: uppercase; letter-spacing: 1.5px; font-size: 8pt; color: #405d6b; }
      h1 { font-size: 23pt; margin: 4px 0; line-height: 1.2; }
      .identity { font-size: 15pt; font-weight: bold; margin: 12px 0 2px; overflow-wrap: anywhere; }
      .period { font-size: 12pt; margin: 4px 0; }
      h2 { font-size: 13pt; border-bottom: 2px solid #8da6b1; padding-bottom: 5px; margin: 20px 0 10px; break-after: avoid; }
      p { margin: 5px 0; overflow-wrap: anywhere; white-space: pre-wrap; }
      .muted, footer { font-size: 8.5pt; color: #435663; }
      .notice { border-left: 3px solid #8da6b1; background: #f3f6f7; padding: 10px 12px; }
      .summary { border: 1px solid #8da6b1; padding: 9px 12px; margin-bottom: 10px; }
      table { width: 100%; border-collapse: collapse; margin: 10px 0; table-layout: fixed; font-size: 9pt; }
      caption { text-align: left; font-weight: bold; padding: 5px 0; break-after: avoid; }
      th, td { border: 1px solid #b5c3ca; padding: 7px 6px; vertical-align: top; overflow-wrap: anywhere; white-space: pre-wrap; }
      th { text-align: left; font-weight: bold; }
      thead th { background: #e7eef1; }
      tbody th { font-weight: normal; }
      tbody tr:nth-child(even) { background: #f7f9fa; }
      tfoot { display: table-row-group; font-weight: bold; background: #e7eef1; }
      tfoot th, tfoot td { border-top: 2px solid #244e60; }
      .number { text-align: right; font-variant-numeric: tabular-nums; }
      .salary th:first-child { width: 65%; }
      .salary-meta { break-inside: avoid; }
      .attendance { font-size: 8.5pt; }
      footer { border-top: 1px solid #8da6b1; padding-top: 10px; margin-top: 22px; break-inside: avoid; }
      @media print {
        thead { display: table-header-group; }
        tr { break-inside: avoid; page-break-inside: avoid; }
        h1, header, .summary, .notice { break-inside: avoid; }
        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      }
    </style></head><body>
    <header><div class="eyebrow">Кадровий облік · Ресторан</div><h1>Бланк працівника</h1>
      <p class="period">Звітний місяць: <strong>${escapeHtml(monthLabel)}</strong> · ${escapeHtml(input.month)}</p>
      <p class="identity">${escapeHtml(input.employee.fullName)}</p><p>${escapeHtml(input.employee.position)}</p>
    </header>
    <section aria-label="Зарплата"><h2>01 / Деталізація зарплати за місяць</h2>${salarySection(input)}</section>
    <section aria-label="Табель"><h2>02 / Табель відвідування</h2>
      <p class="summary">Днів з відмітками: <strong>${days}</strong> · Інтервалів: <strong>${attendance.length}</strong>
        · Збережені години: <strong>${escapeHtml(formatHours(totalHours))}</strong></p>
      <table class="attendance"><caption>Інтервали за робочою датою вибраного місяця</caption>
        <colgroup><col style="width:14%"><col style="width:20%"><col style="width:20%">
          <col style="width:12%"><col style="width:11%"><col style="width:23%"></colgroup>
        <thead><tr><th scope="col">Робоча дата</th><th scope="col">Прихід</th><th scope="col">Вихід</th>
          <th scope="col" class="number">Години</th><th scope="col" class="number">Коеф.</th><th scope="col">Примітка</th></tr></thead>
        <tbody>${attendanceRows || '<tr><td colspan="6">За вибраний місяць відміток табелювання немає.</td></tr>'}</tbody>
        <tfoot><tr><th scope="row" colspan="3">Разом збережених годин</th>
          <td class="number">${escapeHtml(decimal(totalHours))}</td><td colspan="2">${openCount ? `Незавершених інтервалів: ${openCount}` : 'Усі інтервали завершені'}</td></tr></tfoot>
      </table>
      <p class="muted">Дні без відміток не означають прогул або вихідний. Час приходу та виходу — місцевий цивільний час.
        Незавершені інтервали не доповнюються розрахунковими годинами.</p>
    </section>
    <footer>Конфіденційно. Кадрові та зарплатні відомості — лише для уповноважених осіб.
      Зберігайте цей бланк або PDF з обмеженим доступом.</footer>
    </body></html>`;
}
